import { useEffect, useEffectEvent, useState } from "react";

import { signWorkThumbnails } from "./workImages.ts";

/**
 * The photographs for a list of works, signed after the rows arrive.
 *
 * A second pass rather than part of the first, the way every list in this app
 * does it: the list is readable the moment the rows land, and a storage round
 * trip must not hold up the screen. A path that fails to sign is simply
 * omitted and `WorkThumb` draws its empty frame — absent, rather than broken —
 * so the pictures can never fail a screen whose rows already loaded.
 *
 * Nine controllers carried this effect verbatim before it lived here. The map
 * is keyed by WORK id, which is what `signWorkThumbnails` takes everywhere,
 * so `toWork` says where a row keeps its id and image path. It is read through
 * an effect event and never re-signs on its own account: only a new `rows`
 * does, exactly as each caller's own dependency list said.
 */
export function useWorkThumbnails<T>(
  rows: readonly T[] | null,
  toWork: (row: T) => { id: string; imagePath: string | null },
): Map<string, string> {
  const [images, setImages] = useState<Map<string, string>>(new Map());
  const pick = useEffectEvent(toWork);

  useEffect(() => {
    if (!rows) return;
    let active = true;

    void signWorkThumbnails(rows.map((row) => pick(row))).then((signed) => {
      if (active) setImages(signed);
    });

    return () => {
      active = false;
    };
  }, [rows]);

  return images;
}
